import { auditLogApi } from './creatorApi'

export interface AuditChange {
  field: string
  oldValue: any
  newValue: any
}

export interface AuditLogParams {
  actionType:
    | 'created'
    | 'updated'
    | 'deleted'
    | 'status_changed'
    | 'duplicated'
    | 'archived'
    | 'restored'
    | string
  entityType: string
  entityId: string
  entityName: string
  summary?: string
  userName: string
  userEmail: string
  userRole: string
  team: string
  region: string
  changes?: AuditChange[]
  metadata?: any
}

export const auditService = {
  /**
   * Logs an audit event to Zoho Creator.
   * This is a fire-and-forget method that catches errors silently.
   */
  log: async (params: AuditLogParams): Promise<void> => {
    try {
      // Get IP address if possible (best effort client-side)
      let ipAddress = 'unknown'
      try {
        const res = await fetch('https://api.ipify.org?format=json')
        if (res.ok) {
          const data = await res.json()
          ipAddress = data.ip
        }
      } catch (e) {
        // ignore
      }

      await auditLogApi.create({
        Action_Type: params.actionType,
        Entity_Type: params.entityType,
        Entity_ID: params.entityId,
        Entity_Name: params.entityName,
        Summary:
          params.summary ||
          formatAuditSummary(
            params.actionType,
            params.entityType,
            params.entityName,
            params.changes,
          ),
        User_Name: params.userName,
        User_Email: params.userEmail,
        User_Role: params.userRole,
        Team: params.team,
        Region: params.region,
        IP_Address: ipAddress,
        Changes_JSON: params.changes ? JSON.stringify(params.changes) : '',
        Metadata_JSON: params.metadata ? JSON.stringify(params.metadata) : '',
      })
    } catch (err) {
      console.error('Failed to log audit event:', err)
    }
  },

  /**
   * Compares two objects and returns an array of changes for the specified fields.
   */
  diffObjects: (
    oldObj: any,
    newObj: any,
    fieldsToTrack: string[],
  ): AuditChange[] => {
    const changes: AuditChange[] = []

    if (!oldObj || !newObj) return changes

    for (const field of fieldsToTrack) {
      const oldVal = oldObj[field]
      const newVal = newObj[field]

      // Simple string comparison for now. Might need deep equal for complex objects.
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field,
          oldValue: oldVal,
          newValue: newVal,
        })
      }
    }

    return changes
  },
}

/**
 * Generates a human-readable summary of the audit event.
 */
export function formatAuditSummary(
  actionType: string,
  entityType: string,
  entityName: string,
  changes?: AuditChange[],
): string {
  const actionMap: Record<string, string> = {
    created: 'Created',
    updated: 'Updated',
    deleted: 'Deleted',
    status_changed: 'Changed status of',
    duplicated: 'Duplicated',
    archived: 'Archived',
    restored: 'Restored',
  }

  const actionStr = actionMap[actionType] || actionType
  const entityStr = entityType.replace('_', ' ')

  let summary = `${actionStr} ${entityStr} '${entityName}'`

  if (actionType === 'updated' && changes && changes.length > 0) {
    const changedFields = changes.map((c) => c.field).join(', ')
    summary += ` — changed ${changedFields}`
  }

  return summary
}
